import { makeAutoObservable, runInAction } from 'mobx'
import { decorate, injectable, inject } from 'inversify'

import { Api } from '@evertel/api'
import { APIDataDepartmentAgreement, APIDataAgreementDocument, APIDataAgreementAssignment, APIDataDepartmentHistory } from '@evertel/types'
import { RoomStore } from '@evertel/stores'
import { DepartmentAgreementStore } from '../store'

// A single settings-change event (DepartmentHistory action
// 'agreement_settings_updated'). The generic history type carries a loose
// `data` blob; here it holds the agreement's from/to changeSet.
type AgreementSettingsHistory = APIDataDepartmentHistory & {
    id?: number
    blueUserId?: number
    createdBy?: APIDataAgreementDocument['createdBy']
    data?: {
        departmentAgreementId?: number
        agreementName?: string
        changeSet?: Record<string, { from: unknown, to: unknown }>
    }
}

// Discriminated timeline row: content versions and settings changes merged into
// one time-ordered list for the version history modal.
type AgreementTimelineEntry =
    | { type: 'content', createdDate?: string, document: APIDataAgreementDocument }
    | { type: 'settings', createdDate?: string, history: AgreementSettingsHistory }

class AgreementController {
    documents: APIDataAgreementDocument[] = []
    settingsHistory: AgreementSettingsHistory[] = []
    assignments: APIDataAgreementAssignment[] = []
    departmentId: number | null = null
    agreementIdsWithAcceptances: Set<number> = new Set()
    // Room names resolved for history display. Deliberately kept out of
    // RoomStore: the drawer treats store contents as the user's room list,
    // so history-only rooms must not be written there.
    roomNamesById: Record<number, string> = {}
    private requestedRoomIds: Set<number> = new Set()

    constructor(
        private api: Api,
        private departmentAgreementStore: DepartmentAgreementStore,
        private roomStore: RoomStore
    ) {
        makeAutoObservable(this)
    }

    init(departmentId: number) {
        this.departmentId = departmentId
    }

    get agreements(): APIDataDepartmentAgreement[] {
        if (!this.departmentId) return []
        return this.departmentAgreementStore.find(a => a.departmentId === this.departmentId)
    }

    fetchAgreements = async () => {
        const departmentId = this.departmentId
        if (!departmentId) return

        const results = await this.api.Routes.Department.getAgreements(departmentId, {
            include: ['currentDocument']
        })

        const counts = await Promise.allSettled(
            (results ?? []).map(a =>
                this.api.Routes.Department.getAgreementAcceptancesSearchCount(
                    departmentId, { departmentAgreementId: a.id }
                )
            )
        )

        runInAction(() => {
            this.departmentAgreementStore.update(results ?? [])
            this.agreementIdsWithAcceptances = new Set(
                (results ?? [])
                    .filter((a, i) => {
                        const c = counts[i]
                        return a.id !== undefined && c.status === 'fulfilled' && (c.value?.count ?? 0) > 0
                    })
                    .map(a => a.id as number)
            )
        })
    }

    fetchDocumentHistory = async (agreementId: number) => {
        const results = await this.api.Routes.DepartmentAgreement.getDocuments(agreementId, {
            order: 'version DESC',
            include: ['createdBy']
        })

        runInAction(() => {
            this.documents = results ?? []
        })

        return results ?? []
    }

    fetchSettingsHistory = async (agreementId: number) => {
        const results = await this.api.Routes.DepartmentAgreement.getSettingsHistory(agreementId)

        runInAction(() => {
            this.settingsHistory = (results ?? []) as AgreementSettingsHistory[]
        })

        return results ?? []
    }

    // Clear per-agreement history so a re-opened modal never shows stale rows
    // from a previously viewed agreement before its fetches resolve.
    resetHistory = () => {
        this.documents = []
        this.settingsHistory = []
    }

    // Content versions and settings changes merged into a single newest-first
    // timeline. MobX memoizes this getter, so it only recomputes when either
    // source list changes.
    get timeline(): AgreementTimelineEntry[] {
        const entries: AgreementTimelineEntry[] = [
            ...this.documents.map(document => ({
                type: 'content' as const,
                createdDate: document.createdDate as unknown as string | undefined,
                document
            })),
            ...this.settingsHistory.map(history => ({
                type: 'settings' as const,
                createdDate: history.createdDate as unknown as string | undefined,
                history
            }))
        ]

        return entries.sort((a, b) => {
            const at = a.createdDate ? new Date(a.createdDate).getTime() : 0
            const bt = b.createdDate ? new Date(b.createdDate).getTime() : 0
            return bt - at
        })
    }

    fetchAssignments = async (agreementId: number) => {
        const results = await this.api.Routes.DepartmentAgreement.getAssignments(agreementId)

        runInAction(() => {
            this.assignments = results ?? []
        })

        return results ?? []
    }

    // Resolve room names for a list of ids. Names already in RoomStore or the
    // local cache are reused; only the remainder is fetched, in a single call.
    // Ids that were already requested (including deleted rooms the API won't
    // return) are skipped so repeat calls never refetch.
    //
    // Returns only the rooms that could be resolved, in input order, so
    // callers using this as select state (e.g. EditAgreementModal) naturally
    // drop stale references to deleted rooms. History display instead uses
    // roomLabel(), which falls back to the raw id for unresolvable rooms.
    fetchRoomNames = async (roomIds: number[]): Promise<{ id: number, name: string }[]> => {
        const ids = [...new Set(roomIds)]
        if (!this.departmentId || !ids.length) return []

        const missing: number[] = []

        runInAction(() => {
            for (const id of ids) {
                if (this.roomNamesById[id] !== undefined || this.requestedRoomIds.has(id)) continue

                const storeName = this.roomStore.findById(id)?.name
                if (storeName) {
                    this.roomNamesById[id] = storeName
                } else {
                    missing.push(id)
                    this.requestedRoomIds.add(id)
                }
            }
        })

        if (missing.length) {
            const rooms = await this.api.Routes.Department.getRooms(this.departmentId, {
                where: { id: { inq: missing } },
                fields: ['id', 'name']
            })

            runInAction(() => {
                for (const room of rooms ?? []) {
                    if (room.id !== undefined && room.name) {
                        this.roomNamesById[room.id] = room.name
                    }
                }
            })
        }

        return ids
            .filter(id => this.roomNamesById[id] !== undefined)
            .map(id => ({ id, name: this.roomNamesById[id] }))
    }

    // Label for a room id in history views: resolved name if known,
    // otherwise fall back to the raw id (e.g. deleted rooms).
    roomLabel = (roomId: number): string => {
        return this.roomNamesById[roomId]
            ?? this.roomStore.findById(roomId)?.name
            ?? `#${roomId}`
    }

    createAgreement = async (data: {
        name: string
        content: string
        roomIds?: number[]
        restrictedRoles?: string[]
        acceptOncePerRoom?: boolean
        enabled?: boolean
    }) => {
        if (!this.departmentId) return undefined

        const result = await this.api.Routes.Department.postAgreements(this.departmentId, {
            name: data.name,
            content: data.content,
            ...(data.roomIds !== undefined && { roomIds: data.roomIds }),
            ...(data.restrictedRoles !== undefined && { restrictedRoles: data.restrictedRoles }),
            ...(data.acceptOncePerRoom !== undefined && { acceptOncePerRoom: data.acceptOncePerRoom }),
            ...(data.enabled !== undefined && { enabled: data.enabled })
        })

        runInAction(() => {
            this.departmentAgreementStore.update([result])
        })

        return result
    }

    updateContent = async (agreementId: number, data: { name: string, content: string }) => {
        const result = await this.api.Routes.DepartmentAgreement.putUpdateContent(agreementId, data)

        runInAction(() => {
            this.departmentAgreementStore.update([result])
        })

        return result
    }

    updateSettings = async (agreementId: number, settings: {
        enabled?: boolean
        acceptOncePerRoom?: boolean
        restrictedRoles?: string[]
        roomIds?: number[]
    }) => {
        const result = await this.api.Routes.DepartmentAgreement.putUpdateSettings(agreementId, settings)

        runInAction(() => {
            this.departmentAgreementStore.update([result])
        })

        return result
    }

    deleteAgreement = async (agreementId: number) => {
        await this.api.Routes.DepartmentAgreement.delById(agreementId)

        runInAction(() => {
            this.departmentAgreementStore.deleteById(agreementId)
        })
    }
}

decorate(injectable(), AgreementController)
decorate(inject(Api), AgreementController, 0)
decorate(inject(DepartmentAgreementStore), AgreementController, 1)
decorate(inject(RoomStore), AgreementController, 2)

export { AgreementController }
export type { AgreementSettingsHistory, AgreementTimelineEntry }
